import { getSupabase } from "../auth/supabaseClient";
import { walletService } from "../wallet/walletService";
import { solanaService } from "../wallet/solanaService";
import type { PremiumSource, StaminaState } from "./staminaTypes";

export interface PersistedStaminaRecord {
  current: number;
  updatedAtMs: number;
}

interface NormalizedStaminaRecord {
  record: PersistedStaminaRecord;
  nextRecoveryAt: number | null;
  changed: boolean;
}

export const STAMINA_MAX = 3;
export const STAMINA_RECOVERY_INTERVAL_MS = 20 * 60 * 1000;
export const STAMINA_LOCAL_STORAGE_KEY = "bitbeast.stamina.v1";
export const PREMIUM_ETH_CONTRACT = "0xb586d1dc01178ce79046e00ede62a3218722dfdd";
export const PREMIUM_SOL_MINT = "2zMMhcVQEXDtdE6vsFS7S7D5oUodfJHE8vd1gnBouauv";
export const PREMIUM_SOL_MIN_BALANCE = 100;

function clampInt(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.floor(value)));
}

function defaultRecord(now: number): PersistedStaminaRecord {
  return {
    current: STAMINA_MAX,
    updatedAtMs: now
  };
}

function hasStorage(): boolean {
  try {
    return typeof localStorage !== "undefined";
  } catch {
    return false;
  }
}

export function applyStaminaRecovery(
  input: PersistedStaminaRecord | null | undefined,
  now: number
): NormalizedStaminaRecord {
  const safeNow = Number.isFinite(now) ? now : Date.now();
  if (input == null) {
    return {
      record: defaultRecord(safeNow),
      nextRecoveryAt: null,
      changed: true
    };
  }

  const current = clampInt(Number(input.current) || 0, 0, STAMINA_MAX);
  const rawUpdatedAtMs = Number(input.updatedAtMs);
  const updatedAtMs = Number.isFinite(rawUpdatedAtMs)
    ? Math.min(rawUpdatedAtMs, safeNow)
    : safeNow;

  if (current >= STAMINA_MAX) {
    const record = {
      current: STAMINA_MAX,
      updatedAtMs
    };
    return {
      record,
      nextRecoveryAt: null,
      changed: record.current !== input.current || record.updatedAtMs !== input.updatedAtMs
    };
  }

  const elapsedMs = Math.max(0, safeNow - updatedAtMs);
  const recovered = Math.floor(elapsedMs / STAMINA_RECOVERY_INTERVAL_MS);
  if (recovered <= 0) {
    return {
      record: { current, updatedAtMs },
      nextRecoveryAt: updatedAtMs + STAMINA_RECOVERY_INTERVAL_MS,
      changed: current !== input.current || updatedAtMs !== input.updatedAtMs
    };
  }

  const recoveredCurrent = current + recovered;
  if (recoveredCurrent >= STAMINA_MAX) {
    return {
      record: {
        current: STAMINA_MAX,
        updatedAtMs: safeNow
      },
      nextRecoveryAt: null,
      changed: true
    };
  }

  const normalizedUpdatedAtMs = updatedAtMs + (recovered * STAMINA_RECOVERY_INTERVAL_MS);
  return {
    record: {
      current: recoveredCurrent,
      updatedAtMs: normalizedUpdatedAtMs
    },
    nextRecoveryAt: normalizedUpdatedAtMs + STAMINA_RECOVERY_INTERVAL_MS,
    changed: true
  };
}

export function resolvePremiumSource(params: {
  eth: {
    address: string | null;
    isVerified: boolean;
    isBitbeastOwner: boolean;
  };
  sol: {
    address: string | null;
    isConnected: boolean;
    tokenBalances: Array<{ mint: string; balance: number }>;
  };
}): PremiumSource | null {
  const { eth, sol } = params;
  const ethPremium = Boolean(eth.address && eth.isVerified && eth.isBitbeastOwner);
  if (ethPremium) {
    return "eth_nft";
  }

  if (!sol.isConnected || !sol.address) {
    return null;
  }
  const premiumToken = sol.tokenBalances.find((token) => token.mint === PREMIUM_SOL_MINT);
  if (premiumToken != null && premiumToken.balance >= PREMIUM_SOL_MIN_BALANCE) {
    return "sol_token";
  }
  return null;
}

export class StaminaService {
  private currentUserId: string | null = null;
  private record: PersistedStaminaRecord = defaultRecord(Date.now());
  private nowProvider: () => number = () => Date.now();
  private initialized = false;

  async init(userId: string | null): Promise<StaminaState> {
    this.currentUserId = userId != null && userId !== "guest-local"
      ? userId
      : null;
    this.record = this.currentUserId != null
      ? await this.loadFromSupabase(this.currentUserId)
      : this.loadFromLocalStorage();
    this.initialized = true;
    return this.normalizeAndGetState();
  }

  getState(): StaminaState {
    return this.normalizeAndGetState();
  }

  /** Returns false until init() has resolved to prevent stale-state checks. */
  canPlay(): boolean {
    if (!this.initialized) return false;
    const state = this.getState();
    return state.premiumActive || state.current > 0;
  }

  async consumeRound(): Promise<StaminaState> {
    const state = this.normalizeAndGetState();
    if (state.premiumActive || this.record.current <= 0) {
      return state;
    }
    this.record = {
      current: Math.max(0, this.record.current - 1),
      updatedAtMs: this.now()
    };
    await this.persist();
    return this.getState();
  }

  async restoreFromAd(): Promise<StaminaState> {
    this.normalizeAndGetState();
    this.record = {
      current: Math.min(STAMINA_MAX, this.record.current + 1),
      updatedAtMs: this.now()
    };
    await this.persist();
    return this.getState();
  }

  /** Synchronous restore — updates in-memory state immediately, persists in background. */
  restoreFromAdOptimistic(): void {
    this.normalizeAndGetState();
    this.record = {
      current: Math.min(STAMINA_MAX, this.record.current + 1),
      updatedAtMs: this.now()
    };
    void this.persist();
  }

  private normalizeAndGetState(): StaminaState {
    const normalized = applyStaminaRecovery(this.record, this.now());
    if (normalized.changed) {
      this.record = normalized.record;
      void this.persist();
    } else {
      this.record = normalized.record;
    }

    const premiumSource = resolvePremiumSource({
      eth: walletService.getState(),
      sol: solanaService.getState()
    });

    return {
      current: this.record.current,
      max: STAMINA_MAX,
      nextRecoveryAt: normalized.nextRecoveryAt,
      premiumActive: premiumSource != null,
      premiumSource
    };
  }

  private now(): number {
    return this.nowProvider();
  }

  private async loadFromSupabase(userId: string): Promise<PersistedStaminaRecord> {
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("profiles")
        .select("stamina_current, stamina_updated_at")
        .eq("id", userId)
        .single();
      if (error != null || data == null) {
        return this.loadFromLocalStorage();
      }
      const updatedAtMs = data.stamina_updated_at != null
        ? Date.parse(String(data.stamina_updated_at))
        : this.now();
      const staminaCurrent = Number(data.stamina_current);
      return {
        current: clampInt(Number.isFinite(staminaCurrent) ? staminaCurrent : STAMINA_MAX, 0, STAMINA_MAX),
        updatedAtMs: Number.isFinite(updatedAtMs) ? updatedAtMs : this.now()
      };
    } catch {
      return this.loadFromLocalStorage();
    }
  }

  private loadFromLocalStorage(): PersistedStaminaRecord {
    if (!hasStorage()) {
      return defaultRecord(this.now());
    }
    try {
      const raw = localStorage.getItem(STAMINA_LOCAL_STORAGE_KEY);
      if (raw == null) {
        return defaultRecord(this.now());
      }
      const parsed = JSON.parse(raw) as Partial<PersistedStaminaRecord>;
      const storedCurrent = Number(parsed.current);
      return {
        current: clampInt(Number.isFinite(storedCurrent) ? storedCurrent : STAMINA_MAX, 0, STAMINA_MAX),
        updatedAtMs: Number.isFinite(Number(parsed.updatedAtMs))
          ? Number(parsed.updatedAtMs)
          : this.now()
      };
    } catch {
      return defaultRecord(this.now());
    }
  }

  private async persist(): Promise<void> {
    if (this.currentUserId != null) {
      await this.saveToSupabase(this.currentUserId);
      return;
    }
    this.saveToLocalStorage();
  }

  private async saveToSupabase(userId: string): Promise<void> {
    try {
      const supabase = getSupabase();
      await supabase
        .from("profiles")
        .update({
          stamina_current: this.record.current,
          stamina_updated_at: new Date(this.record.updatedAtMs).toISOString()
        })
        .eq("id", userId);
    } catch {
      // Ignore profile persistence failures and keep the in-memory state moving.
    }
  }

  private saveToLocalStorage(): void {
    if (!hasStorage()) {
      return;
    }
    try {
      localStorage.setItem(STAMINA_LOCAL_STORAGE_KEY, JSON.stringify(this.record));
    } catch {
      // ignore storage failures
    }
  }

  _setNowForTesting(nowMs: number | null): void {
    this.nowProvider = nowMs == null ? () => Date.now() : () => nowMs;
  }

  _resetForTesting(): void {
    this.currentUserId = null;
    this.nowProvider = () => Date.now();
    this.record = defaultRecord(this.now());
    this.initialized = false;
  }
}

export const staminaService = new StaminaService();
